import { useFormikContext } from 'formik';
import get from 'lodash/get';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { X } from '@phosphor-icons/react';

import FormErrorMessage from '@tmh-website/components/shared/FormErrorMessage';
import { Color } from '@tmh-website/shared/colors';
import type { FormElementCommonProps, FormElementThemeColor } from '@tmh-website/ui/FormElements';
import { StyledFormElements } from '@tmh-website/ui/FormElements';

export interface InputProps
  extends FormElementCommonProps,
    Partial<Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'prefix'>> {
  helpText?: string | ReactNode;
  elementLeft?: ReactNode;
  elementRight?: ReactNode;
  containerRef?: StyledFormElements.ContainerProps['ref'];
  centeredText?: boolean;
  smallLabel?: boolean;
  format?: (value: string | number) => string | number;
  hasClearButton?: boolean;
  hideErrorMessage?: boolean;
  addCustomOnBlur?: () => void;
  labelFontStyle?: 'normal' | 'italic' | 'oblique';
  readOnly?: boolean;
  prefix?: ReactNode;
}

const Input = ({
  themeColor = Color.Neutral_0_White as FormElementThemeColor,
  disabled,
  helpText,
  elementLeft,
  elementRight,
  label,
  name,
  containerRef,
  required,
  placeholder,
  smallLabel,
  centeredText,
  format,
  hasClearButton,
  value,
  hideErrorMessage,
  addCustomOnBlur,
  labelFontStyle = 'normal',
  readOnly,
  prefix,
  ...props
}: InputProps) => {
  const uniqueId = useId();
  const nameWithId = name + '_' + uniqueId;
  const { errors, touched, setFieldValue } = useFormikContext<any>();
  const hasError = get(errors, name) && get(touched, name);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const hasCustomFormat = !!format;
  const fieldIsFilled = value !== '' && value !== undefined && value !== null;

  useEffect(() => {
    if (value !== '' && value !== undefined && value !== null && inputRef.current) {
      setFieldValue(name, value);
    }
  }, []);

  return (
    <div>
      <StyledFormElements.Container
        $themeColor={themeColor}
        $hasError={hasError}
        $isDisabled={disabled || readOnly}
        ref={containerRef}>
        {elementLeft && <StyledFormElements.ExtraElement>{elementLeft}</StyledFormElements.ExtraElement>}
        {label && (
          <StyledFormElements.InputLabel
            $themeColor={themeColor}
            $fieldIsFilled={smallLabel || fieldIsFilled}
            $hasElementLeft={!!elementLeft}
            $centeredText={!!centeredText}
            $labelFontStyle={labelFontStyle}
            htmlFor={nameWithId}>
            {label}
            {required && '*'}
          </StyledFormElements.InputLabel>
        )}
        <StyledFormElements.InputContainer>
          {(isFocused || fieldIsFilled) && Boolean(prefix) && (
            <StyledFormElements.Prefix>{prefix}</StyledFormElements.Prefix>
          )}
          <StyledFormElements.Input
            name={name}
            id={nameWithId}
            $smallPaddingRight={!!elementRight || hasClearButton}
            $smallPaddingLeft={!!elementLeft}
            $smallLabel={!!smallLabel}
            $centeredText={!!centeredText}
            $themeColor={themeColor}
            $hasPrefix={Boolean(prefix)}
            $withoutLabel={!label}
            placeholder={placeholder}
            value={hasCustomFormat && fieldIsFilled && !isFocused && typeof value !== 'object' ? format(value) : value}
            ref={inputRef}
            {...{ disabled, readOnly }}
            {...props}
            type={hasCustomFormat && !isFocused ? 'text' : props.type}
            onFocus={e => {
              setIsFocused(true);
              if (props.onFocus) {
                props.onFocus(e);
              }
            }}
            onBlur={e => {
              setIsFocused(false);
              if (props.onBlur) {
                if (addCustomOnBlur) {
                  addCustomOnBlur();
                }
                props.onBlur(e);
              }
            }}
          />
          {hasClearButton && (
            <StyledFormElements.ClearButton
              as='button'
              onClick={() => setFieldValue(name, '')}
              $isVisible={value !== ''}>
              <X size={16} weight='bold' />
            </StyledFormElements.ClearButton>
          )}
        </StyledFormElements.InputContainer>
        {elementRight && <StyledFormElements.ExtraElement>{elementRight}</StyledFormElements.ExtraElement>}
      </StyledFormElements.Container>
      {helpText && <StyledFormElements.HelpText $themeColor={themeColor}>{helpText}</StyledFormElements.HelpText>}
      {!hideErrorMessage && hasError && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={name} />
        </StyledFormElements.ErrorMessage>
      )}
    </div>
  );
};

export default Input;
